import { motion } from "framer-motion";
import { Sparkles, Shield, DollarSign, Users, Clock, Award } from "lucide-react";
import { siteConfig } from "@/lib/site-config";

const reasons = [
  {
    icon: Sparkles,
    title: "Detail-Driven Cleaning",
    desc: "We follow a checklist on every visit so nothing is missed — kitchens, bathrooms, baseboards, high-touch surfaces, the works.",
    accent: "primary" as const,
  },
  {
    icon: Shield,
    title: "Insured & Professional",
    desc: "Our cleaners are trained, insured, and bring their own supplies. Your home and business are safe with our team.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "Honest, Transparent Pricing",
    desc: "Free written estimates with no hidden fees — and returning customers save 20% on their next cleaning.",
    accent: "secondary" as const,
  },
  {
    icon: Clock,
    title: `Open ${siteConfig.hours.days}`,
    desc: `We clean ${siteConfig.hours.days.toLowerCase()}, ${siteConfig.hours.time}, and stay closed ${siteConfig.hours.closed}. Early mornings, weekends, and after-hours commercial visits — we work around your schedule.`,
    accent: "primary" as const,
  },
  {
    icon: Award,
    title: "Inland Empire Locals",
    desc: `Headquartered in ${siteConfig.address.city}, ${siteConfig.address.state} and serving ${siteConfig.serviceAreas.length} cities across San Bernardino County, Riverside County, and eastern Los Angeles County.`,
    accent: "secondary" as const,
  },
  {
    icon: Users,
    title: "Bilingual, Family-Run",
    desc: "Our family-run team stands behind every clean. Friendly bilingual customer service in English and Spanish.",
    accent: "primary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why {siteConfig.name}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-primary">Us</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons families and businesses across the Inland Empire trust us to keep their spaces spotless.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                reason.accent === "secondary"
                  ? "bg-primary text-primary-foreground"
                  : "bg-primary/20 text-primary"
              }`}>
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">{reason.title}</h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
